import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import { useContext, useEffect, useState } from 'react';
import cToF from 'celsius-to-fahrenheit';

import { useTrend } from '../../hooks/useTrend';
import TrendChart, { dummyTrend } from '../chart/Trend';
import { AppContext } from '../../context/AppContext';
import { useRest } from '../../hooks/useRest';
import '../../stylesheets/Chart.css';
import moment from 'moment';
import { measSet } from '../../templates/consts';
import TrendControls from './TrendControls';
import { useSession } from '../../hooks/useSessionDev';

HighchartsMore(Highcharts);

interface Props {
  datapoint: any;
}

export const intervals = [86400, 3600, 600];

const Trend: React.FC<Props> = ({ datapoint }) => {

  const { appData, setAppData } = useContext(AppContext);

  const [req] = useRest();
  const [sessionData] = useSession();
  const [trendData, setTrendData, getDeviceTrends, genChartOptions] = useTrend('datapoint', datapoint);

  // const [trendData, setTrendData] = useState<any>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [interval, setInterval] = useState<number>(86400);
  const [endTms, setEndTms] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<any[]>([0]);
  const [tmsRange, setTmsRange] = useState<any[]>([]);
  const [pivotPoint, setPivotPoint] = useState<number>(Date.now()/1000)

  // ! FOR 2026 DEMO
  useEffect(() => {
    const closerToStartOrEnd = (utcSeconds: number, spanMinutes: number = 2): 'start' | 'end' => {
      const spanSeconds = spanMinutes * 60;
      const positionInSpan = utcSeconds % spanSeconds;
      return positionInSpan < spanSeconds / 2 ? 'start' : 'end';
    }
    const now = Math.round(Date.now());
    const lagBehind = closerToStartOrEnd(now) === 'start';
    setPivotPoint(now + (lagBehind ? -120 : 120));
  }, [])

  const initTrend = async (endTms: number | false, dir?: string | null) => {
    setAppData({ ...appData, loading: { visible: true } })
    let trend = false;

    // TODO: add cahceing logic for trend data
    // let loc = localStorage.getItem('breeziProTrendData');
    // const localTrends = loc ? JSON.parse(loc) : {};'

    let data = await getDeviceTrends(interval, endTms, dir ?? null);
    data = data.map((item: any) => {
      if (item?.data?.dpr) delete item.data.dpr;
      return item;
    });
    trend = data;

    setTimeout(() => setAppData({ ...appData, loading: { visible: false } }), 300)
    return trend;
  }

  const initGranularUpdate = async (index: number, data: any) => {
    if (interval !== 600) {
      const tmsSet = data.map((item: any) => item.tms);
      const ts = tmsSet[index];
      setEndTms(ts);
      setLoaded(false);
      setInterval(intervals[intervals.indexOf(interval) + 1]);
    }
  }

  const handleTrendNav = async (dir: string) => {
    setLoaded(false);
    const rng = [...tmsRange];
    const newTms = dir === 'forward'
      ? parseInt(rng[rng.length - 1])
      : parseInt(rng[0])
    let bcs = breadcrumbs;
    bcs[intervals.indexOf(interval)] = newTms;
    setBreadcrumbs(bcs);
    const newData = await options(newTms, dir);
    setTrendData(newData);
    setLoaded(true)
  }

  useEffect(() => {
    (async () => {
      setLoaded(false);
      if (interval !== 86400) {
        const intervalIndex = intervals.indexOf(interval);
        let newData;
        if (breadcrumbs[intervalIndex]) {
          newData = await options(parseInt(breadcrumbs[intervalIndex]));
          setBreadcrumbs(breadcrumbs.splice(0, intervalIndex + 1));
        } else {
          setBreadcrumbs([...breadcrumbs, endTms])
          newData = await options(endTms);
        }
        setTrendData(newData);
        setAppData({ ...appData, loading: { visible: false } });
      } else {
        const data = await options(null);
        setTrendData(data);
        const tmsStart = parseInt(tmsRange[0]);
        setBreadcrumbs([tmsStart]);
      }
    })();
  }, [interval]);

  // ! FOR 2026 DEMO
  const logDate = (tms: number) => moment(tms * 1000).format('MM/DD h:mma')
  const calcSyntheticPivotTms = (tms: number) => {
    const originalPivot = 1727348400;
    const origDiff = originalPivot - tms;
    const adjustedTms = Math.round((pivotPoint - origDiff) * 1000);
    console.log({
      tms: logDate(tms),
      pivotPoint: logDate(pivotPoint),
      originalPivot: logDate(originalPivot),
      origDiff: origDiff,
      adjustedTms: logDate(originalPivot)
    })
    return adjustedTms;
  }

  const options = async (tms: number | null, dir?: string) => {
    const data = await initTrend(tms ?? false, dir ?? null) as any;
    console.log(data.at(-1).tms)
    const formattedLabels = data.map((d: any) => moment(calcSyntheticPivotTms(d.tms)).format(interval === 86400 ? 'MM/DD/YYYY' : 'hh:mma : M/D'));
    setTmsRange(data.map((d: any) => d.tms));
    const opts = genChartOptions(formattedLabels, 'datapoint', interval, initGranularUpdate, data);
    return opts;
  }

  useEffect(() => {
    if (trendData) {
      setLoaded(true)
    }
  }, [trendData]);

  useEffect(() => {
    setAppData({ ...appData, loading: { visible: true } });
  }, []);

  useEffect(() => {
    if (trendData) {
      setLoaded(false);
      setTimeout(() => setLoaded(true), 1000);
    }
  }, [window.innerWidth, window.innerHeight]);

  return (
    <div className='dp-panel dp-trend-panel' style={{ overflow: 'visible' }}>
      <TrendControls
        interval={interval}
        setInterval={setInterval}
        breadcrumbs={breadcrumbs}
        handleTrendNav={handleTrendNav}
        tmsRange={tmsRange} />
      {
        loaded && trendData && <TrendChart componentOptions={trendData} />
      }
    </div>
  )

}

export default Trend;